// Governance → Defaults & Workout — the cross-portfolio CASE queue (one
// row per open DefaultCase; fewer, heavier rows than the sibling
// cockpits). Anatomy (five-second rule): summary strip → severity-sorted
// case table → recently-closed track record. Read-only aggregation: every
// action deep-links to the loan's Defaults tab (?tab=defaults&case={id}),
// which owns all mutations. Stage vocabulary is imported from the loan
// tab's caseStages mapping — never copied.

import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Card,
  EmptyState,
  FilterChips,
  SearchField,
  SortSelect,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import {
  formatCurrency,
  formatDateInTz,
  isPastCalendarDay,
} from "@/design-system/lib/format";
import { useDefaultsWorkQueue } from "@/hooks/compliance/useWorkQueues";
import { extractErrorMessage } from "@/api/client";
import type {
  DefaultsClosedCaseDto,
  DefaultsPendingApprovalKind,
  DefaultsWorkQueueRowDto,
  DwM1ReportStatus,
} from "@/api/compliance/workQueueTypes";
import {
  dueDelta,
  SeverityDot,
  SummaryStrip,
} from "@/components/governance/WorkQueuePrimitives";
import { Pagination } from "@/components/layout/Pagination";
import { useDateFormatter } from "@/utils/datetime";
import {
  CASE_STAGES,
  formatCaseStatus,
  stageForStatus,
  type CaseStage,
} from "@/components/portfolioAdmin/loanDetail/defaults/caseStages";

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "urgency", label: "Urgency" },
  { value: "deadline", label: "Deadline" },
  { value: "loan", label: "Loan" },
];

export const PENDING_APPROVAL_LABEL: Record<DefaultsPendingApprovalKind, string> = {
  forbearance: "Forbearance",
  "remediation-plan": "Plan",
  "workout-cost": "Cost",
};

const DWM1_BADGE: Record<DwM1ReportStatus, "finalized" | "draft" | "review" | "error"> = {
  filed: "finalized",
  draft: "draft",
  due: "review",
  overdue: "error",
};

/** Right-aligned tabular money; negatives in parentheses per convention. */
export function money(amount: number): string {
  const formatted = formatCurrency(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
}

export default function DefaultsWorkoutCockpitPage() {
  const [params, setParams] = useSearchParams();
  const stageFilter = params.get("stage") ?? "All";
  const severityFilter = params.get("severity") ?? "All";
  const triggerFilter = params.get("trigger") ?? "All";
  const approvalFilter = params.get("approval") ?? "All";
  const dwm1Filter = params.get("dwm1") ?? "All";
  const loanSearch = params.get("loan") ?? "";
  const sort = params.get("sort") ?? "urgency";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    next.delete("page");
    setParams(next, { replace: true });
  };
  const setParam = (key: string, value: string, defaultValue: string) =>
    writeParams((p) => {
      if (value === defaultValue) p.delete(key);
      else p.set(key, value);
    });
  const setPage = (next: number) =>
    setParams(
      (p) => {
        const out = new URLSearchParams(p);
        out.set("page", String(next));
        return out;
      },
      { replace: true },
    );

  const queueQuery = useDefaultsWorkQueue();
  const allRows = useMemo(() => queueQuery.data?.rows ?? [], [queueQuery.data]);

  // Trigger chips are data-driven — TriggeredBy is a small fixed-ish
  // vocabulary ("payment-delinquency" …) but not an enum.
  const triggers = useMemo(
    () =>
      [...new Set(allRows.map((r) => r.triggeredBy).filter((t): t is string => !!t))].sort(),
    [allRows],
  );

  const filtered = useMemo(() => {
    const search = loanSearch.trim().toLowerCase();
    const rows = allRows
      .filter((r) =>
        stageFilter === "All" ? true : stageForStatus(r.status) === stageFilter,
      )
      .filter((r) =>
        severityFilter === "All" ? true : r.severity === severityFilter,
      )
      .filter((r) =>
        triggerFilter === "All" ? true : r.triggeredBy === triggerFilter,
      )
      .filter((r) =>
        approvalFilter === "All" ? true : r.pendingApprovals.length > 0,
      )
      .filter((r) => (dwm1Filter === "All" ? true : r.dwM1Status === dwm1Filter))
      .filter((r) =>
        search ? (r.loanNumber ?? "").toLowerCase().includes(search) : true,
      );
    if (sort === "deadline") {
      return [...rows].sort((a, b) =>
        (a.nextDeadline ?? "9999-12-31").localeCompare(b.nextDeadline ?? "9999-12-31"),
      );
    }
    if (sort === "loan") {
      return [...rows].sort((a, b) =>
        (a.loanNumber ?? "").localeCompare(b.loanNumber ?? ""),
      );
    }
    // "urgency" keeps the server's canonical order: overdue deadline >
    // pending approval > days-in-stage descending.
    return rows;
  }, [allRows, stageFilter, severityFilter, triggerFilter, approvalFilter, dwm1Filter, loanSearch, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const summary = queueQuery.data?.summary;
  const closed = queueQuery.data?.recentlyClosed ?? [];

  return (
    <div className="flex flex-col gap-space-4" data-testid="defaults-workout-cockpit">
      <Card>
        <h1 className="font-display font-bold text-h2 m-0">Defaults &amp; Workout</h1>
        <p className="text-caption text-gray-mid mt-space-1 m-0">
          Every open default case across the portfolio — stage, money
          position, deadlines, approvals, monthly reporting. Work happens on
          each loan&apos;s Defaults tab — rows link through.
        </p>
      </Card>

      {summary && (
        <SummaryStrip
          data-testid="defaults-summary-strip"
          tiles={[
            { id: "open-cases", label: "Open cases", value: summary.openCases },
            {
              id: "exposure",
              label: "Total exposure",
              value: summary.totalExposure,
              display: money(summary.totalExposure),
            },
            {
              id: "net-position",
              label: `Net (recoveries ${money(summary.totalNetRecoveries)} − costs ${money(summary.totalCosts)})`,
              value: summary.totalNetRecoveries - summary.totalCosts,
              display: money(summary.totalNetRecoveries - summary.totalCosts),
              tone:
                summary.totalNetRecoveries - summary.totalCosts < 0
                  ? "error"
                  : "success",
            },
            {
              id: "deadline-week",
              label: "Deadline ≤ 7 days",
              value: summary.deadlineDueWithin7Days,
              tone: "warning",
            },
            {
              id: "overdue",
              label: "Deadline overdue",
              value: summary.deadlineOverdue,
              tone: "error",
            },
            {
              id: "approvals",
              label: "Approvals pending",
              value: summary.casesWithPendingApproval,
            },
            {
              id: "dwm1-unfiled",
              label: "DW-M1 unfiled this period",
              value: summary.dwM1Unfiled,
              tone: "warning",
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-col gap-space-3">
          <FilterChips
            label="Filter by stage"
            chips={[
              { id: "All", label: "All stages" },
              ...CASE_STAGES.map((s) => ({ id: s.key, label: s.label })),
            ]}
            activeId={stageFilter}
            onChange={(id) => setParam("stage", id, "All")}
          />
          {triggers.length > 1 && (
            <FilterChips
              label="Filter by trigger"
              chips={[
                { id: "All", label: "All triggers" },
                ...triggers.map((t) => ({ id: t, label: formatCaseStatus(t) })),
              ]}
              activeId={triggerFilter}
              onChange={(id) => setParam("trigger", id, "All")}
            />
          )}
          <div className="flex flex-wrap items-center gap-space-3">
            <FilterChips
              label="Filter by severity"
              chips={[
                { id: "All", label: "All severities" },
                { id: "error", label: "Overdue deadline" },
                { id: "warning", label: "Needs attention" },
              ]}
              activeId={severityFilter}
              onChange={(id) => setParam("severity", id, "All")}
            />
            <FilterChips
              label="Filter by pending approval"
              chips={[
                { id: "All", label: "All cases" },
                { id: "pending", label: "Has pending approval" },
              ]}
              activeId={approvalFilter}
              onChange={(id) => setParam("approval", id, "All")}
            />
            <FilterChips
              label="Filter by DW-M1 status"
              chips={[
                { id: "All", label: "All DW-M1" },
                { id: "filed", label: "Filed" },
                { id: "draft", label: "Draft" },
                { id: "due", label: "Due" },
                { id: "overdue", label: "Overdue" },
              ]}
              activeId={dwm1Filter}
              onChange={(id) => setParam("dwm1", id, "All")}
            />
            <SearchField
              label="Search by loan number"
              placeholder="Loan number…"
              value={loanSearch}
              onChange={(v) => setParam("loan", v, "")}
              className="w-56"
            />
            <SortSelect
              options={SORT_OPTIONS}
              value={sort}
              onChange={(v) => setParam("sort", v, "urgency")}
            />
          </div>
        </div>
      </Card>

      {queueQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">Loading case queue…</p>
        </Card>
      ) : queueQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {extractErrorMessage(queueQuery.error)}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        allRows.length === 0 ? (
          <div data-testid="defaults-queue-empty">
            <EmptyState
              title="No open default cases"
              description="Every loan in the portfolio is outside default workout — the calm good news."
            />
          </div>
        ) : (
          <div data-testid="defaults-queue-filtered-empty">
            <EmptyState
              variant="noResults"
              title="No cases match the current filters"
              description="Clear the stage, severity, trigger, or loan filters to see the full queue."
            />
          </div>
        )
      ) : (
        <Card className="p-0 overflow-x-auto">
          <Table data-testid="defaults-queue-table">
            <THead>
              <TR>
                <TH className="w-8">
                  <span className="sr-only">Severity</span>
                </TH>
                <TH>Loan</TH>
                <TH>Stage</TH>
                <TH className="text-right">Days in stage</TH>
                <TH>Trigger</TH>
                <TH className="text-right">Exposure</TH>
                <TH className="text-right">Costs</TH>
                <TH className="text-right">Recoveries</TH>
                <TH className="text-right">Net</TH>
                <TH>Next deadline</TH>
                <TH>Pending approval</TH>
                <TH>DW-M1</TH>
              </TR>
            </THead>
            <TBody>
              {pageRows.map((r) => (
                <CaseRow key={r.caseId} row={r} />
              ))}
            </TBody>
          </Table>
          {pageCount > 1 && (
            <div className="px-space-3 pb-space-3">
              <Pagination
                page={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={filtered.length}
                totalPages={pageCount}
                onPrev={() => setPage(currentPage - 1)}
                onNext={() => setPage(currentPage + 1)}
              />
            </div>
          )}
        </Card>
      )}

      <ClosedCasesSection cases={closed} />
    </div>
  );
}

function StageChip({ status }: { status: string }) {
  const stage = stageForStatus(status);
  const label = CASE_STAGES.find((s) => s.key === stage)?.label ?? stage;
  return (
    <span
      data-stage={stage satisfies CaseStage}
      title={formatCaseStatus(status)}
      className="inline-flex items-center px-space-2 h-6 rounded-full text-caption font-bold bg-onyx text-white-pure whitespace-nowrap"
    >
      {label}
    </span>
  );
}

function CaseRow({ row }: { row: DefaultsWorkQueueRowDto }) {
  const navigate = useNavigate();
  const target = `/portfolio-admin/loans/${row.loanId}?tab=defaults&case=${row.caseId}`;
  return (
    <TR
      data-testid={`defaults-queue-row-${row.caseId}`}
      className="cursor-pointer hover:bg-white-warm"
      onClick={() => navigate(target)}
    >
      <TD>{row.severity ? <SeverityDot severity={row.severity} /> : null}</TD>
      <TD>
        <Link
          to={target}
          className="font-semibold text-small text-onyx hover:text-gold-shadow hover:underline"
          data-testid={`defaults-queue-link-${row.caseId}`}
          onClick={(e) => e.stopPropagation()}
        >
          {row.loanNumber ?? "View loan"}
        </Link>
        {row.borrowerName && (
          <p className="text-caption text-gray-mid m-0 truncate max-w-[16rem]">
            {row.borrowerName}
          </p>
        )}
      </TD>
      <TD>
        <StageChip status={row.status} />
      </TD>
      <TD className="text-right text-small tabular-nums">{row.daysInStage}d</TD>
      <TD className="text-small">
        {row.triggeredBy ? formatCaseStatus(row.triggeredBy) : "—"}
      </TD>
      <TD className="text-right text-small tabular-nums whitespace-nowrap">
        {money(row.exposure)}
      </TD>
      <TD className="text-right text-small tabular-nums whitespace-nowrap">
        {money(row.totalCosts)}
      </TD>
      <TD className="text-right text-small tabular-nums whitespace-nowrap">
        {money(row.netRecoveries)}
      </TD>
      <TD
        className={`text-right text-small tabular-nums whitespace-nowrap font-semibold ${
          row.netPosition < 0 ? "text-status-error" : ""
        }`}
      >
        {money(row.netPosition)}
      </TD>
      <TD className="whitespace-nowrap text-small">
        {row.nextDeadline ? (
          // 🔴 ONE CELL, ONE RULE. The colour and the label are two readings of
          // the same question and they used to be computed differently: the
          // class compared the raw `DateOnly` against the UTC day (`todayIso`),
          // while `dueDelta` resolved days against Phoenix. From 17:00 Phoenix
          // the cell painted overdue red over its own label saying "Due today".
          // Moving only the colour would not have closed it — the label would
          // still resolve in Phoenix, so a non-Phoenix viewer would acquire a
          // NEW split at their own 17:00. Both halves now take the viewer's
          // zone, so they cannot disagree in any zone.
          <span
            className={
              isPastCalendarDay(row.nextDeadline)
                ? "text-status-error font-semibold"
                : "text-status-warning"
            }
            title={row.nextDeadlineSource ?? undefined}
          >
            {dueDelta(row.nextDeadline)}
          </span>
        ) : (
          "—"
        )}
      </TD>
      <TD>
        {row.pendingApprovals.length === 0 ? (
          <span className="text-caption text-gray-mid">—</span>
        ) : (
          <span className="flex flex-wrap gap-space-1">
            {row.pendingApprovals.map((kind) => (
              <Badge key={kind} status="review">
                {PENDING_APPROVAL_LABEL[kind]}
              </Badge>
            ))}
          </span>
        )}
      </TD>
      <TD>
        <Badge status={DWM1_BADGE[row.dwM1Status]}>{row.dwM1Status}</Badge>
      </TD>
    </TR>
  );
}

function ClosedCasesSection({ cases }: { cases: DefaultsClosedCaseDto[] }) {
  const { tz } = useDateFormatter();
  return (
    <Card data-testid="defaults-closed-section">
      <details>
        <summary className="cursor-pointer font-display font-bold text-h4">
          Recently closed cases (90 days) · {cases.length}
        </summary>
        {cases.length === 0 ? (
          <p className="text-small text-gray-mid italic m-0 mt-space-2">
            No cases closed in the last 90 days.
          </p>
        ) : (
          <Table data-testid="defaults-closed-table" className="mt-space-2">
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Closed</TH>
                <TH>Resolution</TH>
                <TH className="text-right">Costs</TH>
                <TH className="text-right">Recoveries</TH>
                <TH className="text-right">Net recovery</TH>
              </TR>
            </THead>
            <TBody>
              {cases.map((c) => (
                <TR key={c.caseId} data-testid={`defaults-closed-row-${c.caseId}`}>
                  <TD>
                    <Link
                      to={`/portfolio-admin/loans/${c.loanId}?tab=defaults&case=${c.caseId}`}
                      className="text-small text-gold-shadow hover:underline"
                    >
                      {c.loanNumber ?? "View loan"}
                    </Link>
                    {c.borrowerName && (
                      <p className="text-caption text-gray-mid m-0 truncate max-w-[16rem]">
                        {c.borrowerName}
                      </p>
                    )}
                  </TD>
                  <TD className="text-small whitespace-nowrap">
                    {c.closedDate ? formatDateInTz(c.closedDate, tz) : "—"}
                  </TD>
                  <TD className="text-small">
                    {c.resolutionType ? formatCaseStatus(c.resolutionType) : "—"}
                  </TD>
                  <TD className="text-right text-small tabular-nums">
                    {money(c.totalCosts)}
                  </TD>
                  <TD className="text-right text-small tabular-nums">
                    {money(c.netRecoveries)}
                  </TD>
                  <TD
                    className={`text-right text-small tabular-nums font-semibold ${
                      c.netPosition < 0 ? "text-status-error" : "text-status-success"
                    }`}
                  >
                    {money(c.netPosition)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </details>
    </Card>
  );
}
